import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { LookupService } from "src/app/shared/services";

// One role with its granted feature keys, whether it's a built-in (can't be deleted) and how many
// active users hold it. Mirrors backend RoleAdminDto.
export interface IRoleAdmin {
    id: number;
    description: string;
    isBuiltIn: boolean;
    userCount: number;
    menus: string[];
    features: string[];
}

// A gateable area in the feature catalog. Mirrors backend FeatureDefinition.
export interface IFeatureDefinition {
    key: string;
    displayName: string;
    description: string;
}

// A top-level menu + the feature keys inside it. Mirrors backend MenuDefinition.
export interface IMenuDefinition {
    key: string;
    displayName: string;
    icon: string;
    featureKeys: string[];
}

// Create (id null) or update a role + the complete desired set of granted menus and feature keys.
export interface IRoleSaveRequest {
    id?: number | null;
    description: string;
    menus: string[];
    features: string[];
}

@Injectable({ providedIn: 'root' })
export class RolesService {

    private apiBaseEndpoint: string = environment.adminApiUrl;

    constructor(private httpClient: HttpClient, private lookupService: LookupService) { }

    public getRoles(): Observable<IRoleAdmin[]> {
        return this.httpClient.get<IRoleAdmin[]>(`${this.apiBaseEndpoint}/role-admin`);
    }

    public getFeatures(): Observable<IFeatureDefinition[]> {
        return this.httpClient.get<IFeatureDefinition[]>(`${this.apiBaseEndpoint}/role-admin/features`);
    }

    public getMenus(): Observable<IMenuDefinition[]> {
        return this.httpClient.get<IMenuDefinition[]>(`${this.apiBaseEndpoint}/role-admin/menus`);
    }

    // After any role change, drop the cached role-types lookup so the new/renamed role appears
    // immediately in the User Administration dropdown (the backend busts its cache too).
    public createRole(request: IRoleSaveRequest): Observable<number> {
        return this.httpClient.post<number>(`${this.apiBaseEndpoint}/role-admin`, request)
            .pipe(tap(() => this.lookupService.clearRoleTypes()));
    }

    public updateRole(roleId: number, request: IRoleSaveRequest): Observable<boolean> {
        return this.httpClient.put<boolean>(`${this.apiBaseEndpoint}/role-admin/${roleId}`, request)
            .pipe(tap(() => this.lookupService.clearRoleTypes()));
    }

    public deleteRole(roleId: number): Observable<boolean> {
        return this.httpClient.delete<boolean>(`${this.apiBaseEndpoint}/role-admin/${roleId}`)
            .pipe(tap(() => this.lookupService.clearRoleTypes()));
    }
}
